import { NodeId, TraitLabel, TreeView } from '@fluid-experimental/tree';
import { assertPrecondition, mfsError } from '../../../errors';
import { COLLECTION_VALUES, MfsCollectionNode, MfsReferenceNode, NodeKind } from '..';

import { getNode, getNodes, hasNone } from './deltaUtility';
import { isCollectionNode, isReferenceNode } from '../typeGuards';
import { findParentCollection } from '../lookup';
import { DeltaNodeGroup, Match, MfsChange } from './types';
import { MfsCollectionEntry } from '../../../store/mfsTypes';
import { logVerbose } from '../../../log';
import { deltaMove } from './deltaMove';

/** Checks if the given delta/changes identify a collection change event. */
export function matchCollectionChanged(
  view: TreeView,
  previousView: TreeView,
  delta: DeltaNodeGroup
): Match<MfsChange[]> {
  const hasAdd = !hasNone(delta, 'added');
  const hasRemove = !hasNone(delta, 'removed');
  if (!hasAdd && !hasRemove) {
    logVerbose('matchCollectionChanged', 'move element match');
    // move element
    return hasElementMoved(view, previousView);
  }

  if (hasAdd && !hasRemove) {
    logVerbose('matchCollectionChanged', 'add element match');
    // add element
    return hasElementChanged(view, delta, 'add');
  }

  if (hasRemove && !hasAdd) {
    logVerbose('matchCollectionChanged', 'remove element match');
    // remove element
    return hasElementChanged(previousView, delta, 'remove');
  }

  logVerbose('matchCollectionChanged', 'match collection failed');
  return Match.fail();
}

/** Checks whether the given change is a collection element move. */
export function hasElementMoved(view: TreeView, previousView: TreeView): Match<MfsChange[]> {
  const result = getMovedNodeId(view, previousView);

  if (result.isOK) {
    const { movedId, parentId, previousParentId } = result.value;
    logVerbose('hasElementMoved', 'move nodeId result is Ok', { movedId, parentId, previousParentId });

    const collection = findParentCollection(view, movedId);
    const previousCollection = parentId === previousParentId ? collection : findParentCollection(previousView, movedId);
    assertPrecondition(!!collection, () => mfsError.parentCollectionNotFound(movedId, 'Current'));
    assertPrecondition(!!previousCollection, () => mfsError.parentCollectionNotFound(movedId, 'Previous'));

    const isMovedAmongCollections = previousCollection.identifier !== collection.identifier;
    logVerbose('hasElementMoved', 'collections ids', {
      previousCollectionIdentifier: previousCollection.identifier,
      collectionIdentifier: collection.identifier,
    });

    return Match.ok([
      {
        collectionId: collection.payload.id,
        label: collection.payload.label,
        kind: 'collectionChanged',
        changeKind: 'moveElement',
        ...(parentId !== collection.identifier && { parentId }),
        ...(previousParentId !== previousCollection.identifier && { previousParentId }),
        ...(isMovedAmongCollections && { previousCollectionId: previousCollection.payload.id }),
        ...(isMovedAmongCollections && { previousCollectionLabel: previousCollection?.payload.label }),
      },
    ]);
  }

  logVerbose('hasElementMoved', 'hasElementMoved match failed');

  return Match.fail();
}

function getMovedNodeId(
  view: TreeView,
  previousView: TreeView
): Match<{ movedId: NodeId; previousParentId: NodeId; parentId: NodeId }> {
  const deltaMap = deltaMove(previousView, view);
  if (deltaMap.size === 1) {
    const [[key, value]] = deltaMap.entries();
    if (value.source && value.destination) {
      return Match.ok({
        movedId: key,
        previousParentId: value.source.parent,
        parentId: value.destination.parent,
      });
    }
  }

  logVerbose('getMovedNodeId', `Unexpected deltaMap`, { deltaMap });
  return Match.fail();
}

function hasElementChanged(view: TreeView, delta: DeltaNodeGroup, changeKind: 'add' | 'remove'): Match<MfsChange[]> {
  const matchCollectionOrParent = getEitherCollectionOrParentNode(delta);

  if (matchCollectionOrParent.isOK === false) {
    logVerbose('hasElementChanged', 'matchCollectionOrParent failed');
    return matchCollectionOrParent;
  }

  const { collectionNode, parentNode } = locateAddRemoveChange(view, matchCollectionOrParent.value);
  logVerbose('hasElementChanged', '', {
    collectionId: collectionNode.payload.id,
    parentId: parentNode?.identifier,
    changeKind,
  });

  return Match.ok([
    {
      collectionId: collectionNode.payload.id,
      label: collectionNode.payload.label,
      kind: 'collectionChanged',
      ...(parentNode && { parentId: parentNode.identifier }),
      changeKind: changeKind === 'add' ? 'addElement' : isEmpty(collectionNode) ? 'clear' : 'removeElement',
      elements: getElements(delta, changeKind),
    },
  ]);
}

function getElements(delta: DeltaNodeGroup, changeKind: 'add' | 'remove'): readonly MfsCollectionEntry[] {
  const referenceNodes = getNodes(delta, changeKind, NodeKind.Reference);
  return referenceNodes.map((referenceNode) => {
    assertPrecondition(isReferenceNode(referenceNode));
    return {
      itemId: referenceNode.payload,
      elementId: referenceNode.identifier,
    };
  });
}

function isEmpty(collectionNode: MfsCollectionNode): boolean {
  return !collectionNode.traits.has(COLLECTION_VALUES);
}

function locateAddRemoveChange(
  view: TreeView,
  collectionOrParentNode: MfsCollectionNode | MfsReferenceNode
): { collectionNode: MfsCollectionNode; parentNode?: MfsReferenceNode } {
  if (!isReferenceNode(collectionOrParentNode)) {
    return { collectionNode: collectionOrParentNode };
  }
  const parentNode = collectionOrParentNode;
  // this could be at any depth, so need to get parent until we find the collection.
  const collectionNode = findParentCollection(view, parentNode.identifier);
  assertPrecondition(!!collectionNode && isCollectionNode(collectionNode));
  return { collectionNode, parentNode };
}

function getEitherCollectionOrParentNode(delta: DeltaNodeGroup): Match<MfsCollectionNode | MfsReferenceNode> {
  const changedItemCount = delta.changed[NodeKind.Item]?.length ?? 0;
  const changedReferenceCount = delta.changed[NodeKind.Reference]?.length ?? 0;
  logVerbose('getEitherCollectionOrParentNode', 'item and reference cound', {
    changedItemCount,
    changedReferenceCount,
  });

  if (!(changedItemCount + changedReferenceCount <= 1)) {
    return Match.fail();
  }

  return changedItemCount > 0
    ? getNode(0, delta, NodeKind.Item, isCollectionNode)
    : getNode(0, delta, NodeKind.Reference, isReferenceNode);
}
